import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Navigate } from "react-router-dom";
import { useForm, useFieldArray, Form } from "react-hook-form";
import "./style.css";
import DocumentUploadForm from "../../components/DocumentUploadForm/index.js";
import InputMask from "../../components/InputMask/index.js";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { NumericFormat } from "react-number-format";
import { TbDoorExit } from "react-icons/tb";
import { jwtDecode } from "jwt-decode";
import { usePermissoes } from "./../../../hooks/usePermissoes.js";
import { API_ENDPOINTS, API_BASE_URL } from "../../../js/api.js";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import { Check } from "lucide-react";

const LINK_DOCUSIGN_FALLBACK =
  "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=72bb8440-acc9-4e15-b87e-f80823d9d92f&env=na2&acct=272fb0e3-8eca-4797-ac82-b4ba543c625f&v=2";
const schema = yup.object().shape({
  email: yup
    .string()
    .email("O campo e-mail é inválido")
    .required("O campo e-mail é obrigatório"),
});

const STATUS_LABELS = {
  AGUARDANDO_ESTUDANTE: "Aguardando preenchimento do Estudante",
  AGUARDANDO_VALIDACAO_CIEEMG: "Aguardando validação do CIEEMG",
  EM_VALIDACAO: "Em validação pelo CIEEMG",
  AGUARDANDO_CONFECCAO: "Aguardando confecção do contrato",
  CONTRATO_CONFECCIONADO: "Contrato confeccionado",
  AGUARDANDO_ASSINATURAS: "Aguardando assinaturas",
  DISPONIBILIZADO_PARA_ASSINATURA: "Disponibilizado para assinatura",
  FINALIZADO: "Finalizado",
  REPROVADO_EMPRESA: "Reprovado — voltar p/ Empresa",
  REPROVADO_ESTUDANTE: "Reprovado — voltar p/ Estudante",
  REPROVADO_DEFINITIVO: "Reprovado definitivamente",
  PENDENTE: "Pendente",
  APROVADO: "Aprovado",
  RECUSADO: "Recusado",
};
const STATUS_CORES = {
  AGUARDANDO_ESTUDANTE: "#3b82f6",
  AGUARDANDO_VALIDACAO_CIEEMG: "#3b82f6",
  EM_VALIDACAO: "#1e40af",
  AGUARDANDO_CONFECCAO: "#3b82f6",
  CONTRATO_CONFECCIONADO: "#1e40af",
  AGUARDANDO_ASSINATURAS: "#3b82f6",
  DISPONIBILIZADO_PARA_ASSINATURA: "#1e40af",
  FINALIZADO: "#1e3a8a",
  REPROVADO_EMPRESA: "#ef4444",
  REPROVADO_ESTUDANTE: "#ef4444",
  REPROVADO_DEFINITIVO: "#dc2626",
  PENDENTE: "#3b82f6",
  APROVADO: "#1e3a8a",
  RECUSADO: "#dc2626",
};

const AguardandoEmpresa = () => {
  const navigate = useNavigate();
  return (
    <div className="tce-container tce-py-5 tce-text-center">
      <h2 className="tce-h2">Aguardando Empresa</h2>
      <p
        className="tce-text-muted"
        style={{ maxWidth: "500px", margin: "1rem auto" }}
      >
        Você ainda não tem um TCE para preencher.
        <br />
        <br />A empresa precisa criar o TCE primeiro. Depois você poderá abrir
        aqui e preencher seus dados.
      </p>

      <button
        type="button"
        className="tce-btn tce-btn-secondary"
        onClick={() => navigate("/")}
        style={{ marginTop: "1rem" }}
      >
        Voltar
      </button>
    </div>
  );
};

const TCE = () => {
  const currentUser = JSON.parse(localStorage.getItem("user"));

  const {
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  // Hook para gerenciar notificações toast (substitui alert() por mensagens elegantes)
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    warning: toastWarning,
    removeToast,
  } = useToast();
  // Hook para diálogos de confirmação elegantes (substitui window.confirm)
  const { confirm, confirmDialog } = useConfirm();
  const [formData, setFormData] = useState({
    origem_candidato: "",
    estudante_id: null,
    dt_nascimento_estudante: "",
    empresa: "",
    cnpj_cpf: "",
    telefones: "",
    email_empresa: "",
    nome_estagiario: "",
    cargo_estagiario: "",
    cpf_estagiario: "",
    email_estagiario: "",
    email_supervisor: "",
    formacao_escolar: "",
    telefone_estagiario: "",
    nome_telefone_estudante_aprovado: "",
    estudanteaprovadofoiindicadooucaptado: "",
    curso: "",
    email: "",
    inicio_estagio: "",
    termino_estagio: "",
    termino_estagio_autopreenchido: null,
    valor_bolsa_auxilio: "",
    valor_bolsa_transporte: "",
    local_estagio: "",
    local_realizacao_atividades: "",
    atividades_desenvolvidas: "",
    observacoes1: "",
    observacoes2: "",
    maior_menor_idade: "",
    email_menor_idade: "",
    nome_menor_idade: "",
    telefone_menor_idade: "",
    cpf_menor_idade: "",
    cpf_estudante_estagiario: "",
    rg: "",
    escola: "",
    nome: "",
    nome_termo_compromisso: "",
    unidade: "",
    ano_periodo_serie: "",
    previsao_formatura: "",
    tipo_estagio: "",
    tipo_contrato: "",
    orientador_estagio: "",
    horario_fixo_ou_alternado: "",
    cargo: "",
    email_escola: "",
    cnpj_escola: "",
    horario_fixo_ou_alternado: "",
    nome_filial: "",
    cpfCnpj: "",
    telefones2: "",
    bairro: "",
    municipio: "",
    uf: "",
    emailContratos: "",
    emailFinanceiro: "",
    representadaPor: "",
    cargoRepresentante: "",
    emailRepresentante: "",
    responsavelSupervisor: "",
    cargoSupervisor: "",
    emailSupervisor: "",
  });

  // Calcula a data máxima permitida para o Término do estágio
  // (até 2 anos após o Início). Retorna string no formato YYYY-MM-DD
  // ou "" se o Início não estiver preenchido.
  const calcularMaxTerminoEstagio = () => {
    if (!formData.inicio_estagio) return "";
    const [yy, mm, dd] = formData.inicio_estagio.split("-").map(Number);
    if (!yy || !mm || !dd) return "";
    const limite = new Date(yy, mm - 1, dd);
    limite.setFullYear(limite.getFullYear() + 2);
    return [
      limite.getFullYear(),
      String(limite.getMonth() + 1).padStart(2, "0"),
      String(limite.getDate()).padStart(2, "0"),
    ].join("-");
  };

  // Formata string YYYY-MM-DD para DD/MM/YYYY (exibição).
  const formatarDataBR = (dataStr) => {
    if (!dataStr) return "";
    const [yy, mm, dd] = dataStr.split("-");
    if (!yy || !mm || !dd) return dataStr;
    return `${dd}/${mm}/${yy}`;
  };

  const [authorized, setAuthorized] = useState(false);
  const [declaracaoEscolar, setDeclaracaoEscolar] = useState(null);
  const [error, setError] = useState("");
  const [cargaHoraria, setCargaHoraria] = useState(null);
  const [cargaHorariaSemanal, setCargaHorariaSemanal] = useState(0);
  const {
    permissao,
    podeEditarCampo,
    isEmpresa,
    isEstudante,
    isCieemg,
    tipoLabel,
  } = usePermissoes();
  const [documentoIdentificacao, setDocumentoIdentificacao] = useState(null);
  const [estudantesCieemg, setEstudantesCieemg] = useState([]);
  const [loadingEstudantes, setLoadingEstudantes] = useState(false);
  const [estudanteEncontrado, setEstudanteEncontrado] = useState(false);
  const [showConsentimento, setShowConsentimento] = useState(false);
  const [cienteSemCadastro, setCienteSemCadastro] = useState(false);
  const [loadingCpf, setLoadingCpf] = useState(false);
  const [idadeCalculada, setIdadeCalculada] = useState(null);
  const inputRef = useRef();
  const [filiais, setFiliais] = useState([]);
  const [mostrarFiliais, setMostrarFiliais] = useState(false);
  const [filialLoading, setFilialLoading] = useState(false);
  const [localAtividade, setLocalAtividade] = useState("");
  const [unidadesCieemg, setUnidadesCieemg] = useState([]);
  const [dadosFilialSelecionada, setDadosFilialSelecionada] = useState(null);
  const [nivel, setNivel] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState(null);
  const [progress, setProgress] = useState(0);
  const [anexosFiles, setAnexosFiles] = useState({});
  const [anexosProgress, setAnexosProgress] = useState({});
  const [anexosStatus, setAnexosStatus] = useState({});
  const [msg, setMsg] = useState(null);
  const [val, setVal] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadStatus, setUploadStatus] = useState("select");
  const [user, setUser] = useState({ name: "", email: "" });
  const [status, setStatus] = useState({ type: "", mensagem: "" });
  const [arquivoSupervisor, setArquivoSupervisor] = useState(null);
  const [arquivoSupervisorId, setArquivoSupervisorId] = useState(null);
  const inputSupervisorRef = useRef(null);
  const [horarios, setHorarios] = useState({ tipo: "", dias: [] });
  const [enderecoEmpresa, setEnderecoEmpresa] = useState(null);
  const [showObs1, setShowObs1] = useState(false);
  const [showObs2, setShowObs2] = useState(false);
  const [possuiIncompatibilidade, setPossuiIncompatibilidade] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [tceId, setTceId] = useState(null);
  const [tceStatus, setTceStatus] = useState(null);
  const [tceHistorico, setTceHistorico] = useState([]);
  const [tipoUsuario, setTipoUsuario] = useState(null);
  const [tceIdCarregado, setTceIdCarregado] = useState(null);
  const [tceCarregado, setTceCarregado] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [tcesEstudante, setTcesEstudante] = useState([]);
  const [tcesPendentesCieemg, setTcesPendentesCieemg] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [horario, setHorario] = useState({
    tipo: "",
    diasSelecionados: [],
    fixo: {
      entrada: "",
      saida: "",
      intervaloInicio: "",
      intervaloFim: "",
    },
    alternado: {
      segunda: {
        entrada: "",
        saida: "",
        intervaloInicio: "",
        intervaloFim: "",
      },
      terca: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      quarta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      quinta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      sexta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      sabado: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      domingo: {
        entrada: "",
        saida: "",
        intervaloInicio: "",
        intervaloFim: "",
      },
    },
  });

  // useEffect: carrega dados da filial via GET /filiais/:id quando o usuário
  // seleciona uma FILIAL numérica (replicado do TcaEmpregador — Tipo 2).
  useEffect(() => {
    const filialId = String(localAtividade || "").trim();
    if (!filialId || filialId === "MATRIZ" || isNaN(Number(filialId))) {
      // Limpa os campos de filial quando vazio/MATRIZ
      const camposFilial = [
        "nome_filial",
        "cpfCnpj",
        "telefones",
        "telefones2",
        "bairro",
        "municipio",
        "uf",
        "emailContratos",
        "emailFinanceiro",
        "representadaPor",
        "cargoRepresentante",
        "emailRepresentante",
        "responsavelSupervisor",
        "cargoSupervisor",
        "emailSupervisor",
      ];
      setFormData((prev) => {
        const next = { ...prev };
        camposFilial.forEach((c) => {
          next[c] = "";
        });
        return next;
      });
      setDadosFilialSelecionada(null);
      return;
    }

    let cancelled = false;

    const carregarFilial = async () => {
      setFilialLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.filial_id(filialId);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        const dados = response.data || {};

        // Guarda os dados completos para uso na exibição do card da Filial
        // e para o auto-preenchimento do "Local da realização do estágio".
        setDadosFilialSelecionada(dados);

        const mapeamento = [
          ["nome_filial", dados.nome_filial],
          ["cpfCnpj", dados.cnpj],
          ["telefones", dados.telefone_1],
          ["telefones2", dados.telefone_2],
          ["bairro", dados.bairro],
          ["municipio", dados.cidade],
          ["uf", dados.uf],
          ["emailContratos", dados.email_contratos],
          ["emailFinanceiro", dados.email_financeiro],
          ["representadaPor", dados.representante_nome],
          ["cargoRepresentante", dados.representante_cargo],
          ["emailRepresentante", dados.representante_email],
          ["responsavelSupervisor", dados.supervisor_nome],
          ["cargoSupervisor", dados.supervisor_cargo],
          ["emailSupervisor", dados.supervisor_email],
        ];

        setFormData((prev) => {
          const next = { ...prev };
          mapeamento.forEach(([campo, valor]) => {
            if (valor !== undefined && valor !== null) {
              next[campo] = valor;
            }
          });
          // Auto-preenche "Local da realização do estágio" com o endereço da filial
          // quando o objeto traz `unidadeTeorica.endereco` (mesmo padrão do TCA).
          if (dados.unidadeTeorica && dados.unidadeTeorica.endereco) {
            next.local_estagio = dados.unidadeTeorica.endereco;
          } else if (dados.endereco) {
            // Fallback: usa endereço da própria filial
            const partes = [
              dados.endereco,
              dados.bairro ? `, ${dados.bairro}` : "",
              dados.cidade ? ` - ${dados.cidade}` : "",
              dados.uf ? `/${dados.uf}` : "",
            ].filter(Boolean);
            next.local_estagio = partes.join("");
          }
          return next;
        });

        toastSuccess("Dados da filial carregados.");
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const backendMsg =
          (Array.isArray(err.response?.data?.errors) &&
            err.response.data.errors.join(", ")) ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          null;
        console.error(
          `[TCE][Filial] erro ao carregar filial id=${filialId} status=${status}:`,
          err.response?.data,
        );
        if (status === 404) {
          toastError(
            backendMsg
              ? `Filial não encontrada: ${backendMsg}`
              : "Filial não encontrada",
          );
        } else if (status >= 500) {
          toastError(
            backendMsg
              ? `Erro no servidor ao buscar filial: ${backendMsg}`
              : "Erro no servidor ao buscar filial",
          );
        } else if (status === 401 || status === 403) {
          toastError("Sessão expirada ou sem permissão. Faça login novamente.");
        } else {
          toastError(
            backendMsg
              ? `Erro ao carregar filial: ${backendMsg}`
              : "Erro ao carregar dados da filial",
          );
        }
      } finally {
        if (!cancelled) setFilialLoading(false);
      }
    };

    carregarFilial();
    return () => {
      cancelled = true;
    };
  }, [localAtividade, toastError, toastSuccess]);

  // --- AUTO-PREENCHIMENTO DO "LOCAL DA REALIZAÇÃO DO ESTÁGIO" ---
  // Quando a unidade selecionada for MATRIZ, busca o endereço da unidade
  // do CIEE/MG correspondente à região selecionada (mesmo padrão do TCA).
  // Quando for FILIAL, o useEffect acima já cuida de preencher com o endereço
  // retornado por GET /filiais/:id.
  useEffect(() => {
    const local = formData.local_realizacao_atividades;

    // 1. Reset se nada selecionado
    if (!local || local === "") {
      return;
    }

    // 2. MATRIZ → endereço da unidade CIEE/MG correspondente à região
    if (local === "MATRIZ") {
      let enderecoMatriz = "";

      if (formData.regiao && unidadesCieemg.length > 0) {
        const unidadeEncontrada = unidadesCieemg.find(
          (u) =>
            Number(u.codigo) === Number(formData.regiao) ||
            String(u.codigo) === String(formData.regiao) ||
            String(u.id) === String(formData.regiao) ||
            String(u.codigo_unidade) === String(formData.regiao),
        );
        if (unidadeEncontrada) {
          enderecoMatriz = unidadeEncontrada.endereco;
        }
      }

      if (enderecoMatriz) {
        setFormData((prev) => {
          if (prev.local_estagio === enderecoMatriz) return prev;
          return { ...prev, local_estagio: enderecoMatriz };
        });
      }
    }
  }, [formData.regiao, unidadesCieemg, formData.local_realizacao_atividades]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      window.location.href = "/";
      return;
    }

    try {
      const decoded = jwtDecode(token);

      const nivelAcesso = Number(decoded.acesso_autorizacao ?? 0);
      setNivel(nivelAcesso);

      setFormData((prev) => ({
        ...prev,
        empresa_id: decoded.empresa_id,
      }));

      const niveisPermitidos = [0, 1, 2, 3, 4];

      if (!niveisPermitidos.includes(nivelAcesso)) {
        window.location.href = "/sem-permissao";
        return;
      }

      setAuthorized(true);
    } catch (e) {
      window.location.href = "/";
      return;
    }

    const emojiRegex = /[\p{Extended_Pictographic}]/gu;

    const limparEmoji = function () {
      if (
        this.id === "cnpj-cpf" ||
        this.id === "tce-telefones" ||
        this.id === "tce_rg_estagiario" ||
        this.id === "tce-six-cpf" ||
        this.id === "ano-periodo-serie" ||
        this.id === "previsao-formatura" ||
        this.id === "tce-eight-cnpj" ||
        this.id === "inicio-estagio" ||
        this.id === "termino-estagio" ||
        this.id === "valor_bolsa_auxilio" ||
        this.id === "valor_bolsa_transporte" ||
        this.id === "tce-two-row-cpf"
      )
        return;

      if (this.value) {
        this.value = this.value.replace(emojiRegex, "");
      }
    };

    const handleInputValidation = (inputId, regex) => {
      if (
        inputId === "#cnpj-cpf" ||
        inputId === "#tce-telefones" ||
        inputId === "#tce_rg_estagiario" ||
        inputId === "#tce-six-cpf" ||
        inputId === "#ano-periodo-serie" ||
        inputId === "#tce-eight-cnpj"
      )
        return null;

      const input = document.querySelector(inputId);

      if (input) {
        const handlePaste = function () {
          const self = this;
          setTimeout(() => {
            if (!regex.test(self.value)) self.value = "";
          }, 10);
        };

        const handleKeyPress = function (e) {
          const char = String.fromCharCode(e.keyCode);
          if (!regex.test(char)) e.preventDefault();
        };

        input.addEventListener("paste", handlePaste);
        input.addEventListener("keypress", handleKeyPress);

        return () => {
          input.removeEventListener("paste", handlePaste);
          input.removeEventListener("keypress", handleKeyPress);
        };
      }
    };

    const carregarFiliais = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await axios.get(`${API_BASE_URL}/tca/filiais`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("[Filial TCE] response:", response.data);

        if (response.data?.possuiFiliais) {
          setFiliais(response.data.opcoes);
          setMostrarFiliais(true);
        }
      } catch (error) {
        console.error("[Filial TCE] erro ao carregar filiais:", error);
      }
    };

    const carregarUnidadesCieemg = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/admin-unidades`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUnidadesCieemg(response.data || []);
      } catch (error) {
        // Silencia erros: sem unidades o auto-fill fica vazio (campo fica em branco).
        console.warn("[TCE] Falha ao carregar /admin-unidades:", error?.response?.status || error?.message);
      }
    };

    const decoded = jwtDecode(localStorage.getItem("token"));

    if (decoded.empresa_id) {
      carregarFiliais();
      carregarUnidadesCieemg();
      carregarEnderecoEmpresa();
      carregarDadosEmpresa();
      carregarEstudantesCieemg();
    }

    const removers = [
      handleInputValidation("#estagiario-empresa", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#nome-aprendiz", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#cargo_estagiario", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#email_estagiario", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email_estagiarioo", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#formacao_escolar", /^[0-9a-zA-Z ]+$/),
      handleInputValidation(
        "#nome_telefone_estudante_aprovado",
        /^[0-9a-zA-Z ]+$/,
      ),
      handleInputValidation("#local_estagio", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#escola-faculdade", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce-eight-unidade", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce-eight-curso", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#orientador-estagio", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#cargo", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#nome-estudante-estagiario", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce_nome_termo_compromisso", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce_rg_estagiario", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#ano_periodo_serie", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce_previsao_formatura", /^[0-9a-zA-Z /]*$/),
      handleInputValidation("#tce-eight-cnpj", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#inicio-estagio", /^[0-9a-zA-Z /]*$/),
      handleInputValidation("#termino-estagio", /^[0-9a-zA-Z /]*$/),
      handleInputValidation("#nome-menor-idade", /^[0-9a-zA-ZÀ-ÿ ]+$/),
      handleInputValidation("#email_empresa", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email-menor-idade", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email_escola", /^[a-zA-Z0-9@._-]+$/),
    ];
  }, []);

  useEffect(() => {
    if (formData.local_realizacao_atividades === "MATRIZ") {
      if (enderecoEmpresa) {
        const endereco =
          `${enderecoEmpresa.logradouro}, ${enderecoEmpresa.numero}` +
          `${enderecoEmpresa.complemento ? `, ${enderecoEmpresa.complemento}` : ""}` +
          ` - CEP ${enderecoEmpresa.cep}`;
        setFormData((prev) => ({ ...prev, local_estagio: endereco }));
      }
    } else if (formData.local_realizacao_atividades) {
      setFormData((prev) => ({ ...prev, local_estagio: "" }));
    }

    if (!enderecoEmpresa) {
      return;
    }

    const endereco =
      `${enderecoEmpresa.logradouro}, ${enderecoEmpresa.numero}` +
      `${
        enderecoEmpresa.complemento ? `, ${enderecoEmpresa.complemento}` : ""
      } - CEP ${enderecoEmpresa.cep}`;

    setFormData((prev) => ({
      ...prev,
      local_estagio: endereco,
    }));
  }, [formData.local_realizacao_atividades, enderecoEmpresa]);

  useEffect(() => {
    const { totalSemanal } = validarHorarios(horario);
    setCargaHorariaSemanal(totalSemanal);
  }, [horario]);

  function horasDecimais(
    entrada,
    saida,
    intervaloInicio = "",
    intervaloFim = "",
  ) {
    if (!entrada || !saida) return 0;

    const [eh, em] = entrada.split(":").map(Number);
    const [sh, sm] = saida.split(":").map(Number);
    let totalMin = sh * 60 + sm - (eh * 60 + em);

    if (totalMin < 0) totalMin += 24 * 60;

    if (intervaloInicio && intervaloFim) {
      const [ih, im] = intervaloInicio.split(":").map(Number);
      const [fh, fm] = intervaloFim.split(":").map(Number);
      let intervaloMin = fh * 60 + fm - (ih * 60 + im);
      if (intervaloMin < 0) intervaloMin += 24 * 60;
      totalMin -= intervaloMin;
    }

    return totalMin / 60;
  }

  function validarHorarios(horario) {
    const errors = [];
    let totalSemanal = 0;

    if (horario.tipo === "fixo") {
      const f = horario.fixo;

      if (f.entrada && f.saida) {
        const horas = horasDecimais(
          f.entrada,
          f.saida,
          f.intervaloInicio,
          f.intervaloFim,
        );

        if (horas > 6)
          errors.push("Carga horária diária não pode ultrapassar 6h");
        if (horas >= 6 && (!f.intervaloInicio || !f.intervaloFim)) {
          errors.push("Intervalo obrigatório para jornada de 6h");
        }

        totalSemanal = horas * horario.diasSelecionados.length;
      }
    } else if (horario.tipo === "alternado") {
      const diasMap = {
        segunda: "Segunda",
        terca: "Terça",
        quarta: "Quarta",
        quinta: "Quinta",
        sexta: "Sexta",
        sabado: "Sábado",
        domingo: "Domingo",
      };

      Object.keys(horario.alternado).forEach((key) => {
        const diaLabel = diasMap[key];
        const h = horario.alternado[key];

        if (h.entrada && h.saida) {
          const horas = horasDecimais(
            h.entrada,
            h.saida,
            h.intervaloInicio,
            h.intervaloFim,
          );
          if (horas > 6)
            errors.push(`${diaLabel}: carga horária superior a 6h`);
          if (horas >= 6 && (!h.intervaloInicio || !h.intervaloFim)) {
            errors.push(`${diaLabel}: intervalo obrigatório para 6h`);
          }
          totalSemanal += horas;
        }
      });
    }

    if (totalSemanal > 30) {
      errors.push(
        `Carga horária semanal (${totalSemanal.toFixed(1)}h) superior ao limite de 30h`,
      );
    }
    return { errors, totalSemanal };
  }

  function calcularIdade(dtNasc) {
    if (!dtNasc) return null;
    const dateStr = String(dtNasc).trim();
    let ano, mes, dia;
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      [ano, mes, dia] = dateStr.substring(0, 10).split("-").map(Number);
    } else if (/^\d{2}\/\d{2}\/\d{4}/.test(dateStr)) {
      [dia, mes, ano] = dateStr.split("/").map(Number);
    } else {
      console.warn("Formato de data não reconhecido:", dateStr);
      return null;
    }
    if (!ano || !mes || !dia || isNaN(ano) || isNaN(mes) || isNaN(dia)) {
      console.warn("Data inválida:", dtNasc);
      return null;
    }
    const nasc = new Date(ano, mes - 1, dia);
    if (isNaN(nasc.getTime())) return null;
    const hoje = new Date();
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
    console.log(`[calcularIdade] ${dtNasc} → ${idade} anos`);
    return idade;
  }

  function authHeader() {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }

  function handleChangeEstudanteCieemg(e) {
    const option = e.target.selectedOptions[0];
    const idSelecionado = option?.dataset.id;
    const est = estudantesCieemg.find(
      (s) => String(s.id) === String(idSelecionado),
    );

    if (!est) {
      setFormData((prev) => ({
        ...prev,
        nome_estagiario: "",
        cpf_estudante_estagiario: "",
        rg: "",
        telefone_estagiario: "",
        email_estagiario: "",
        estudante_id: null,
      }));
      setIdadeCalculada(null);
      setFormData((prev) => ({ ...prev, dt_nascimento_estudante: "" }));
      return;
    }

    const idade = calcularIdade(est.dt_nascimento);

    setFormData((prev) => ({
      ...prev,
      nome_estagiario: est.nome,
      cpf_estudante_estagiario: est.cpf,
      rg: est.rg,
      telefone_estagiario: est.telefone1,
      email_estagiario: est.email,
      estudante_id: est.id,
    }));

    setFormData((prev) => ({
      ...prev,
      dt_nascimento_estudante: est.dt_nascimento || "",
    }));
    setIdadeCalculada(idade);
  }

  async function handleChangeCpfEmpresa(e) {
    const cpf = e.target.value;
    setFormData((prev) => ({ ...prev, cpf_estudante_estagiario: cpf }));
    const cpfLimpo = cpf.replace(/\D/g, "");

    if (cpfLimpo.length !== 11) {
      setEstudanteEncontrado(false);
      setShowConsentimento(false);
      setCienteSemCadastro(false);
      setIdadeCalculada(null);
      setFormData((prev) => ({ ...prev, dt_nascimento_estudante: "" }));
      return;
    }

    try {
      setLoadingCpf(true);
      const response = await axios.get(
        `${API_BASE_URL}/tce/buscar-estudante/${cpfLimpo}`,
        { headers: authHeader() },
      );

      if (response.data) {
        const idade = calcularIdade(response.data.dt_nascimento);
        setFormData((prev) => ({
          ...prev,
          nome_estagiario: response.data.nome,
          rg: response.data.rg,
          telefone_estagiario: response.data.telefone1,
          email_estagiario: response.data.email,
          estudante_id: response.data.id,
        }));
        setFormData((prev) => ({
          ...prev,
          dt_nascimento_estudante: response.data.dt_nascimento || "",
        }));
        setIdadeCalculada(idade);
        setEstudanteEncontrado(true);
        setShowConsentimento(false);
        setCienteSemCadastro(true);
      } else {
        handleEstudanteNaoEncontrado();
      }
    } catch (err) {
      if (err.response?.status === 404) {
        handleEstudanteNaoEncontrado();
      } else {
        console.error("Erro ao buscar estudante por CPF:", err);
        toastError("Erro ao buscar estudante. Tente novamente.");
      }
    } finally {
      setLoadingCpf(false);
    }
  }

  function handleEstudanteNaoEncontrado() {
    setFormData((prev) => ({
      ...prev,
      nome_estagiario: "",
      rg: "",
      telefone_estagiario: "",
      email_estagiario: "",
      estudante_id: null,
      dt_nascimento_estudante: "",
    }));
    setIdadeCalculada(null);
    setEstudanteEncontrado(false);
    setShowConsentimento(true);
    setCienteSemCadastro(false);
  }

  // const calcularHoras = (entrada, saida, intervaloInicio, intervaloFim) => {
  //   const [eh, em] = entrada.split(":").map(Number);
  //   const [sh, sm] = saida.split(":").map(Number);
  //   const entradaMin = eh * 60 + em;
  //   const saidaMin = sh * 60 + sm;
  //   let total = saidaMin - entradaMin;

  //   if (intervaloInicio && intervaloFim) {
  //     const [ih, im] = intervaloInicio.split(":").map(Number);
  //     const [fh, fm] = intervaloFim.split(":").map(Number);
  //     total -= (fh * 60 + fm) - (ih * 60 + im);
  //   }
  //   return total / 60;
  // };

  const horarioValido = (hora, minuto) => {
    const h = Number(hora);
    const m = Number(minuto);

    return !isNaN(h) && !isNaN(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59;
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };
  const normalizeCPF = (cpf) => (cpf ? cpf.replace(/\D/g, "") : "");
  const hasDuplicateCPF = (data) => {
    const cpfFields = [
      "cnpj_cpf",
      "cpf_estagiario",
      "cpf_estudante_estagiario",
      "cpf_menor_idade",
    ];
    const values = cpfFields.map((f) => normalizeCPF(data[f]));
    const filtered = values.filter((v) => v !== "");
    return new Set(filtered).size !== filtered.length;
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    let newValue = value;

    if (name === "origem_candidato") {
      setEstudanteEncontrado(false);
      setShowConsentimento(false);
      setCienteSemCadastro(false);
      setIdadeCalculada(null);
      setFormData((prev) => ({ ...prev, dt_nascimento_estudante: "" }));
      setFormData((prev) => ({
        ...prev,
        origem_candidato: value,
        estudante_id: null,
        nome_estagiario: "",
        cpf_estudante_estagiario: "",
        rg: "",
        telefone_estagiario: "",
        email_estagiario: "",
      }));
      return;
    }

    const emojiRegex = /[\p{Extended_Pictographic}]/gu;
    if (
      ![
        "cnpj_cpf",
        "telefones",
        "tce_rg_estagiario",
        "tce-six-cpf",
        "ano_periodo_serie",
        "previsao_formatura",
        "tce-eight-cnpj",
        "valor_bolsa_auxilio",
        "valor_bolsa_transporte",
        "tce-two-row-cpf",
        "nome-menor-idade",
        "email-menor-idade",
        "textarea-observacoes",
      ].includes(name)
    ) {
      newValue = newValue.replace(emojiRegex, "");
    }

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        diasSelecionados: checked
          ? [...prev.diasSelecionados, value]
          : prev.diasSelecionados.filter((dia) => dia !== value),
      }));

      return;
    }

    if (name === "data_inicio_contrato") {
      const [day, month, year] = value.split("/");
      newValue = `20${year}-${month}-${day}`;
    } else if (name === "cnpj_cpf") {
      let formattedValue = value.replace(/\D/g, "");
      if (formattedValue.length <= 11) {
        formattedValue = formattedValue.replace(/(\d{3})(\d)/, "$1.$2");
        formattedValue = formattedValue.replace(/(\d{3})(\d)/, "$1.$2");
        formattedValue = formattedValue.replace(/(\d{3})(\d{1,2})/, "$1-$2");
      } else {
        formattedValue = formattedValue.replace(/^(\d{2})(\d)/, "$1.$2");
        formattedValue = formattedValue.replace(
          /^(\d{2})\.(\d{3})(\d)/,
          "$1.$2.$3",
        );
        formattedValue = formattedValue.replace(/\.(\d{3})(\d)/, ".$1/$2");
        formattedValue = formattedValue.replace(/(\d{4})(\d)/, "$1-$2");
      }
      newValue = formattedValue;
    }

    const regexCampos = [
      "empresa",
      "nome",
      "nome_estagiario",
      "cargo_estagiario",
      "formacao_escolar",
      "nome_telefone_estudante_aprovado",
      "local_estagio",
      "escola",
      "unidade",
      "curso",
      "orientador_estagio",
      "cargo",
      "nome_menor_idade",
    ];
    if (regexCampos.includes(name)) {
      newValue = newValue.replace(/[^0-9a-zA-ZÀ-ÿ\s.,&-]/g, "");
    }

    setFormData((prevData) => {
      const updated = { ...prevData, [name]: newValue };

      // Auto-preencher Término = Início + 2 anos, mas SOMENTE se:
      //  - o usuário alterou o campo Início;
      //  - o Término atual está vazio, OU o Término atual coincide
      //    com o auto-preenchimento anterior (ainda não personalizado).
      if (name === "inicio_estagio" && newValue) {
        try {
          const [yy, mm, dd] = newValue.split("-").map(Number);
          if (yy && mm && dd) {
            const inicioDate = new Date(yy, mm - 1, dd);
            const limite = new Date(inicioDate);
            limite.setFullYear(limite.getFullYear() + 2);
            const limiteStr = [
              limite.getFullYear(),
              String(limite.getMonth() + 1).padStart(2, "0"),
              String(limite.getDate()).padStart(2, "0"),
            ].join("-");

            const terminoAtual = prevData.termino_estagio;
            const terminoJaPersonalizado =
              terminoAtual &&
              terminoAtual !== prevData.termino_estagio_autopreenchido;

            if (!terminoJaPersonalizado) {
              updated.termino_estagio = limiteStr;
              updated.termino_estagio_autopreenchido = limiteStr;
            }
          }
        } catch (_) {
          /* silencia erro de parsing — não bloqueia o form */
        }
      }

      // Se o usuário alterou o Término manualmente, marca como personalizado.
      if (name === "termino_estagio" && newValue) {
        updated.termino_estagio_autopreenchido = null;
      }

      if (hasDuplicateCPF(updated)) {
        setError("⚠️ Você repetiu um CPF em mais de um campo.");
      } else {
        setError("");
      }

      return updated;
    });
    // setFormData((prevData) => ({
    //   ...prevData,
    //   [name]: newValue,
    // }));
  };

  const handleHorarioChange = (tipo, dia, campo, valor) => {
    valor = valor.replace(/\D/g, "");

    setFormData((prev) => {
      if (tipo === "fixo") {
        return {
          ...prev,
          horarioFixo: {
            ...prev.horarioFixo,
            [campo]: valor,
          },
        };
      }

      return {
        ...prev,
        horariosAlternados: {
          ...prev.horariosAlternados,
          [dia]: {
            ...prev.horariosAlternados[dia],
            [campo]: valor,
          },
        },
      };
    });
  };

  const handleArquivoSupervisor = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const formatos = ["application/pdf", "image/png", "image/jpeg"];

    if (!formatos.includes(file.type)) {
      toastWarning("Somente PDF, PNG e JPG são permitidos.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo deve ter no máximo 10MB.");
      return;
    }

    setArquivoSupervisor(file);
  };

  const removerArquivoSupervisor = () => {
    setArquivoSupervisor(null);

    const input = document.getElementById("arquivo-supervisor");

    if (input) {
      input.value = "";
    }
  };

  const isPositiveNumber = (values) => {
    return values.floatValue > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    const decoded = jwtDecode(token);

    if (!decoded?.empresa_id) {
      toastError("Empresa não identificada.");
      return;
    }

    if (!podeEditarCampo("empresa") && !podeEditarCampo("estudante")) {
      toastError("Sem permissão para editar.");
      return;
    }

    if (hasDuplicateCPF(formData)) {
      toastError("Há CPFs repetidos no formulário.");
      return;
    }

    if (!formData.origem_candidato) {
      toastWarning(
        "Selecione a origem do candidato (CIEEMG ou Candidato Próprio).",
      );
      return;
    }

    if (formData.origem_candidato === "CIEEMG" && !formData.estudante_id) {
      toastWarning("Selecione um estudante da lista do CIEEMG.");
      return;
    }

    if (formData.origem_candidato === "EMPRESA") {
      const cpfLimpo = (formData.cpf_estudante_estagiario || "").replace(
        /\D/g,
        "",
      );
      if (cpfLimpo.length !== 11) {
        toastWarning("Informe um CPF válido para o candidato.");
        return;
      }
      if (!estudanteEncontrado && !cienteSemCadastro) {
        toastWarning(
          "É obrigatório confirmar a ciência sobre o cadastro do estudante no CIEEMG.",
        );
        return;
      }
      if (!estudanteEncontrado && !formData.nome_estagiario) {
        toastWarning("Informe o nome do estudante.");
        return;
      }
      if (idadeCalculada === null) {
        toastError("Não foi possível calcular a idade do estudante.");
        return;
      }
    }

    try {
      let linkAssinatura = LINK_DOCUSIGN_FALLBACK;

      try {
        const autorizacao = await getAutorizacao(decoded.empresa_id);
        if (autorizacao?.link) {
          linkAssinatura = autorizacao.link;
        } else if (autorizacao === null) {
          console.warn("Backend sem link — usando fallback DocuSign");
        }
      } catch (e) {
        console.warn(
          "Erro ao buscar autorização — usando fallback DocuSign:",
          e,
        );
      }

      let totalSemanal = 0;
      const validacaoHorario = validarHorarios(horario);
      totalSemanal = validacaoHorario.totalSemanal;
      const horarioErrors = validacaoHorario.errors;

      const dadosEnvio = {
        ...formData,
        idade_calculada: idadeCalculada,
        horario_fixo_ou_alternado: {
          tipo: horario.tipo,
          diasSelecionados: horario.diasSelecionados,
          fixo: horario.fixo,
          alternado: horario.alternado,
          carga_horaria_semanal: totalSemanal,
        },
        local_realizacao_atividades: mostrarFiliais
          ? localAtividade || formData.local_realizacao_atividades || "MATRIZ"
          : "MATRIZ",
      };

      const converterData = (data) => {
        const [dia, mes, ano] = data.split("/");
        return new Date(ano, mes - 1, dia);
      };

      const inicio = converterData(formData.inicio_estagio);
      if (inicio < dataMinimaInicio) {
        toastWarning(
          `A data de início deve ser a partir de ${dataMinimaInicioFormatada}`,
        );
        return;
      }

      const termino = converterData(formData.termino_estagio);
      const limiteFinal = new Date(inicio);
      limiteFinal.setFullYear(limiteFinal.getFullYear() + 2);
      if (termino > limiteFinal) {
        toastWarning("A vigência do estágio não pode ultrapassar 2 anos.");
        return;
      }

      const bolsa =
        Number(
          formData.valor_bolsa_auxilio.replace(/[^\d,]/g, "").replace(",", "."),
        ) || 0;
      if (bolsa <= 0) {
        toastWarning("O valor da bolsa auxílio deve ser maior que zero.");
        return;
      }

      const transporte =
        Number(
          formData.valor_bolsa_transporte
            .replace(/[^\d,]/g, "")
            .replace(",", "."),
        ) || 0;
      if (transporte <= 0) {
        toastWarning("O valor do auxílio transporte deve ser maior que zero.");
        return;
      }

      if (horario.tipo === "") {
        toastWarning("Selecione o tipo de horário (fixo ou alternado).");
        return;
      }

      if (horarioErrors.length > 0) {
        toastWarning("Há erros no horário: " + horarioErrors.join(" • "));
        return;
      }

      const horasValidas = totalSemanal > 0 && totalSemanal <= 30;
      if (!horasValidas) {
        toastWarning(
          `Carga horária semanal inválida: ${totalSemanal.toFixed(1)}h. Limite: 30h.`,
        );
        return;
      }

      if (possuiIncompatibilidade && !arquivoSupervisor) {
        toastWarning(
          "É obrigatório anexar o documento comprobatório de experiência do supervisor.",
        );
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/tce`, dadosEnvio, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const tceId = response.data?.id || response.data?.tceId || "?";

      // Persistir dados da Filial (PUT /filiais/:id) quando o usuário
      // selecionou uma filial numérica e os campos foram preenchidos.
      // Replicado do TcaEmpregador (Tipo 2).
      const filialIdNum = Number(localAtividade);
      const filialPayload = {
        nome_filial: formData.nome_filial,
        cnpj: formData.cpfCnpj,
        telefone_1: formData.telefones,
        telefone_2: formData.telefones2,
        bairro: formData.bairro,
        cidade: formData.municipio,
        uf: formData.uf,
        email_contratos: formData.emailContratos,
        email_financeiro: formData.emailFinanceiro,
        representante_nome: formData.representadaPor,
        representante_cargo: formData.cargoRepresentante,
        representante_email: formData.emailRepresentante,
        supervisor_nome: formData.responsavelSupervisor,
        supervisor_cargo: formData.cargoSupervisor,
        supervisor_email: formData.emailSupervisor,
      };
      if (
        mostrarFiliais &&
        localAtividade &&
        localAtividade !== "MATRIZ" &&
        localAtividade !== "FILIAL" &&
        !isNaN(filialIdNum)
      ) {
        try {
          await axios.put(
            API_ENDPOINTS.filial_update(filialIdNum),
            filialPayload,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          toastSuccess("Dados da filial atualizados.");
        } catch (filialErr) {
          console.warn(
            "Falha ao persistir filial (PUT /filiais/:id):",
            filialErr,
            filialErr?.response?.data,
          );
          const status = filialErr.response?.status;
          const backendMsg =
            filialErr.response?.data?.message ||
            filialErr.response?.data?.erro ||
            filialErr.response?.data?.error ||
            filialErr.message ||
            "Erro ao atualizar dados da filial";
          if (status === 403) {
            toastError(
              "Sem permissão para atualizar a filial. Contate o administrador do sistema.",
            );
          } else if (status === 401) {
            toastError(
              "Sessão expirada. Faça login novamente para atualizar a filial.",
            );
          } else if (status === 404) {
            toastError("Filial não encontrada no servidor.");
          } else if (status >= 500) {
            toastError(`Erro no servidor ao atualizar filial: ${backendMsg}`);
          } else {
            toastError(backendMsg);
          }
        }
      }

      const mensagemSucesso = arquivoSupervisor
        ? `✅ TCE #${tceId} cadastrado com sucesso!\n📎 Comprovante do supervisor enviado.\n\nDeseja abrir o DocuSign para assinatura?`
        : `✅ TCE #${tceId} cadastrado com sucesso!\n\nDeseja abrir o DocuSign para assinatura?`;

      if (arquivoSupervisor) {
        const form = new FormData();
        form.append("foto", arquivoSupervisor);
        form.append("tce_id", response.data.id);
        form.append("tipo_documento", "comprovante_supervisor");

        await axios.post(`${API_BASE_URL}/fotos`, form, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        console.log(response.data);
      }

      if (linkAssinatura) {
        const abrir = await confirm({
          title: "TCE salvo com sucesso!",
          message: mensagemSucesso,
          confirmText: "Abrir DocuSign",
          cancelText: "Depois",
          variant: "success",
        });

        if (abrir) {
          window.open(linkAssinatura, "_blank");
        } else {
          toastSuccess(
            "TCE salvo. Você pode abrir o DocuSign depois pelo menu principal.",
          );
        }
      } else {
        toastWarning(
          `Link de assinatura não disponível. TCE foi salvo com sucesso (#${tceId}). Contate o suporte.`,
        );
      }
    } catch (error) {
      console.error(error);
      toastError("Erro ao salvar o TCE.");
    }
  };

  const clearFileInput = () => {
    inputRef.current.value = "";
    setSelectedFile(null);
    setProgress(0);
    setUploadStatus("select");
  };

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      setUploadStatus("uploading");
      const formData = new FormData();
      formData.append("file", file);

      // "https://apiautorizacao.cieemg.org.br/upload"

      const token = localStorage.getItem("token");
      const decoded = token ? jwtDecode(token) : null;
      const empresaIdToUse = formData.empresa_id || decoded?.empresa_id;

      if (!empresaIdToUse) {
        console.error("Empresa ID inválido");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/tca/empresa/${empresaIdToUse}/autorizacao`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setProgress(percentCompleted);
          },
        },
      );

      console.log("Upload concluído:", response.data);
      setUploadStatus("done");
      clearFileInput();
    } catch (error) {
      console.error("Erro no upload:", error);
    }
  };

  const handleAnexoChange = (key) => async (file) => {
    if (!file) return;

    const allowed = ["application/pdf", "image/png", "image/jpeg"];
    if (!allowed.includes(file.type)) {
      toastWarning("Formato inválido. Use PDF, PNG ou JPG.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo maior que 10MB.");
      return;
    }

    setAnexosFiles((prev) => ({ ...prev, [key]: file }));
    setAnexosStatus((prev) => ({ ...prev, [key]: "uploading" }));
    setAnexosProgress((prev) => ({ ...prev, [key]: 0 }));

    try {
      const token = localStorage.getItem("token");
      const decoded = token ? jwtDecode(token) : null;
      const empresaIdToUse = decoded?.empresa_id;

      if (!empresaIdToUse) {
        console.error("Empresa ID inválido");
        setAnexosStatus((prev) => ({ ...prev, [key]: "error" }));
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      await axios.post(
        `${API_BASE_URL}/tca/empresa/${empresaIdToUse}/autorizacao`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setAnexosProgress((prev) => ({ ...prev, [key]: percentCompleted }));
          },
        }
      );

      setAnexosStatus((prev) => ({ ...prev, [key]: "done" }));
    } catch (error) {
      console.error("Erro no upload:", error);
      setAnexosStatus((prev) => ({ ...prev, [key]: "error" }));
      toastError("Erro ao enviar arquivo. Tente novamente.");
    }
  };

  const isEmpresaDisabled =
    !permissao.podeEditarTudo && !permissao.podeEditarEmpresa;
  const isEstudanteDisabled =
    !permissao.podeEditarTudo && !permissao.podeEditarEstudante;
  const isEmpresaReadOnly =
    permissao?.label === "Empresa" || permissao?.label === "Estudante";

  const carregarEnderecoEmpresa = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(`${API_BASE_URL}/tce/empresa-endereco`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setEnderecoEmpresa(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const carregarDadosEmpresa = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(`${API_BASE_URL}/tce/dados-empresa`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setFormData((prev) => ({
        ...prev,
        empresa: response.data.razaosocial || "",
        cnpj_cpf: response.data.cnpj || "",
        telefones: response.data.telefone || "",
        telefones: formatarTelefone(response.data.telefone) || "",
        cnpj_cpf: formatarCnpj(response.data.cnpj) || "",
        email_empresa: response.data.email || "",
      }));
    } catch (err) {
      console.error("Erro ao carregar dados da empresa:", err);
    }
  };

  const carregarEstudantesCieemg = async () => {
    try {
      setLoadingEstudantes(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/tce/estudantes-empresa`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEstudantesCieemg(response.data || []);
    } catch (err) {
      console.error("Erro ao carregar estudantes do CIEEMG:", err);
    } finally {
      setLoadingEstudantes(false);
    }
  };

  const formatarTelefone = (tel) => {
    if (!tel) return "";
    const nums = tel.replace(/\D/g, "");
    if (nums.length === 11) {
      return nums.replace(/(\d{2})(\d{5})(\d{4})/, "($1)$2-$3");
    } else if (nums.length === 10) {
      return nums.replace(/(\d{2})(\d{4})(\d{4})/, "($1)$2-$3");
    }
    return tel;
  };

  const formatarCnpj = (cnpj) => {
    if (!cnpj) return "";
    const nums = cnpj.replace(/\D/g, "");
    if (nums.length === 14) {
      return nums.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5",
      );
    } else if (nums.length === 11) {
      return nums.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cnpj;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get("id");

    if (id) {
      setModoEdicao(true);
      setTceId(id);
      carregarTceExistente(id);
    }
  }, []);

  const verificarPermissaoTCE = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      const tipo = decoded.tipo;
      const tipoPrograma = Number(decoded.tipo_programa);
      if (tipo === "usuario_ciee") return true;
      if (tipoPrograma === 0) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  if (!verificarPermissaoTCE()) {
    return <Navigate to="/dashboard" replace />;
  }

  const carregarTceExistente = async (id) => {
    try {
      const token = localStorage.getItem("token");

      const responseTce = await axios.get(`${API_BASE_URL}/tce/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTceStatus(responseTce.data.status);
      setFormData({ ...formData, ...responseTce.data });
      if (responseTce.data.local_realizacao_atividades) {
        setLocalAtividade(
          String(responseTce.data.local_realizacao_atividades),
        );
      }

      try {
        const responseHist = await axios.get(
          `${API_BASE_URL}/tce/${id}/historico`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setTceHistorico(responseHist.data.historico || []);
      } catch (err) {
        console.warn(
          "Sem permissão para ver histórico (ok se não for SEOPE/SECEX):",
          err,
        );
        setTceHistorico([]);
      }
    } catch (err) {
      console.error("Erro ao carregar TCE:", err);
      toastError("Erro ao carregar TCE existente.");
    }
  };

  const diasSemana = [
    { label: "Segunda", key: "segunda" },
    { label: "Terça", key: "terca" },
    { label: "Quarta", key: "quarta" },
    { label: "Quinta", key: "quinta" },
    { label: "Sexta", key: "sexta" },
    { label: "Sábado", key: "sabado" },
    { label: "Domingo", key: "domingo" },
  ];

  if (!authorized) return null;

  function checkChar(e) {
    const char = String.fromCharCode(e.keyCode);

    const pattern = "[a-zA-Z0-9 ]";

    if (char.match(pattern)) {
      return true;
    }
  }

  const onSubmit = () => {};

  const iniciarValidacao = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/iniciar-validacao`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Validação iniciada!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(
        err.response?.data?.errors?.[0] || "Erro ao iniciar validação",
      );
    }
  };

  const aprovar = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/aprovar-validacao`,
        {
          observacao: "Validação aprovada",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("TCE aprovado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro ao aprovar");
    }
  };

  const recusar = async () => {
    if (!tceId) return;
    const observacao = prompt("Motivo da recusa:");
    if (!observacao) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/reprovar-validacao`,
        {
          destino: "empresa",
          observacao,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastWarning("TCE reprovado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro ao reprovar");
    }
  };

  const marcarConfeccionado = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/marcar-confecionado`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Contrato marcado como confeccionado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro");
    }
  };

  const marcarDisponibilizado = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/marcar-disponibilizado`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Contrato marcado como disponibilizado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro");
    }
  };

  const finalizar = async () => {
    if (!tceId) return;
    const ok = await confirm({
      title: "Finalizar processo",
      message: "Confirmar finalização do processo?\nEsta ação é definitiva.",
      confirmText: "Sim, finalizar",
      cancelText: "Cancelar",
      variant: "default",
    });
    if (!ok) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/finalizar`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Processo finalizado com sucesso!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro");
    }
  };

  const submeterValidacaoEstudante = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/submeter-validacao-estudante`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("TCE submetido para validação do CIEEMG!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro");
    }
  };

  // const aprovar = async () => {
  //   try {
  //     console.log("Aprovando...");

  //     // await api.put('/tce/aprovar', { id });

  //     alert("Aprovado com sucesso!");
  //   } catch (err) {
  //     console.error(err);
  //     alert("Erro ao aprovar");
  //   }
  // };

  // const recusar = async () => {
  //   try {
  //     console.log("Recusando...");
  //     // await api.put('/tce/recusar', { id });

  //     alert("Recusado com sucesso!");
  //   } catch (err) {
  //     console.error(err);
  //     alert("Erro ao recusar");
  //   }
  // };

  // `https://apiautorizacao.cieemg.org.br/tce/empresa/${empresaId}/autorizacao`,

  const getAutorizacao = async (empresaId) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/tce/empresa/${empresaId}/autorizacao`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Erro ao buscar autorização:", error);
      return null;
    }
  };

  const hoje = new Date();

  const dataMinimaInicio = new Date();
  dataMinimaInicio.setDate(hoje.getDate() + 5);
  const dataMinimaInicioFormatada =
    dataMinimaInicio.toLocaleDateString("pt-BR");
  const dataMinimaInicioFormatadaISO = dataMinimaInicio
    .toISOString()
    .split("T")[0];

  const handleSupervisorFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const tiposPermitidos = ["application/pdf", "image/png", "image/jpeg"];

    if (!tiposPermitidos.includes(file.type)) {
      toastWarning("Formato inválido. Use PDF, PNG ou JPG.");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo maior que 10MB.");
      e.target.value = "";
      return;
    }

    setArquivoSupervisor(file);
  };

  // const handleArquivo = (e) => {
  //   setArquivo(e.target.files[0]);
  // };

  // const removerArquivoSupervisor = () => {

  //   setArquivoSupervisor(null);

  //   if (inputSupervisorRef.current) {
  //     inputSupervisorRef.current.value = "";
  //   }

  // };

  const visualizarArquivoSupervisor = () => {
    console.log("arquivoSupervisor:", arquivoSupervisor);

    if (!arquivoSupervisor) {
      console.log("Arquivo inexistente");
      return;
    }

    const url = URL.createObjectURL(arquivoSupervisor);

    console.log("URL:", url);

    window.open(url, "_blank");
  };

  return (
    <>
      <form className="form-tce form-tce-fade tce-container" onSubmit={handleSubmit}>
        <h2 className="tce-h2">TCE — Programa Estágio</h2>
        <div className="tce-card">
          <div className="div-dados-empresa-title tce-card-header">
            <label htmlFor="dados-empresa-title">Dados da Empresa</label>
          </div>

          <div className="tce-card-body">
            {permissao.podeEditarEmpresa && (
              <div className="tce-one">
                <div className="div-estagiario-empresa">
                  <label className="all-label" htmlFor="empresa">
                    Empresa
                  </label>
                  <input
                    type="text"
                    name="empresa"
                    className="estagiario-empresa"
                    id="estagiario-empresa"
                    readOnly={isEmpresaReadOnly}
                    onChange={handleChange}
                    value={formData.empresa}
                  />
                </div>
              </div>
            )}

            <div className="tce-one-1">
              <div className="div-tce-cpf-cnpj">
                <label className="all-label" htmlFor="cnpj_cpf">
                  CNPJ ou CPF
                </label>
                <input
                  type="text"
                  name="cnpj_cpf"
                  className="cnpj-cpf"
                  id="cnpj-cpf"
                  maxLength={18}
                  readOnly={isEmpresaReadOnly}
                  onChange={handleChange}
                  value={formData.cnpj_cpf}
                  required
                />
              </div>

              <div className="div-tce-telefones">
                <label className="all-label" htmlFor="telefones">
                  Telefones
                </label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  type="text"
                  name="telefones"
                  autoComplete="off"
                  className="tce-telefones"
                  id="tce-telefones"
                  readOnly={isEmpresaReadOnly}
                  onChange={handleChange}
                  value={formData.telefones || ""}
                  required
                />
              </div>

              <div className="div-tce-email">
                <label className="all-label" htmlFor="email_empresa">
                  Email
                </label>
                <input
                  type="email"
                  name="email_empresa"
                  className="tce-email"
                  id="email_empresa"
                  maxLength={80}
                  required
                  onChange={handleChange}
                  readOnly={isEmpresaReadOnly}
                  value={formData.email_empresa}
                />
                <span>{errors.email?.message}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-supervisor-estagiario-title tce-card-header">
            <label htmlFor="supervisor-estagiario-title">
              Supervisor do Estagiário
            </label>
          </div>

          <div className="tce-card-body">
            <div className="tce-two">
              <div className="tce-two-row">
                <div className="div-tce-nome-estagiario">
                  <label className="all-label" htmlFor="nome">
                    Nome
                  </label>
                  <input
                    type="text"
                    name="nome"
                    className="nome-aprendiz"
                    id="nome-aprendiz"
                    maxLength={200}
                    disabled={isEmpresaDisabled}
                    onChange={handleChange}
                    value={formData.nome}
                    required
                  />
                </div>

                <div className="div-cargo-estagiario">
                  <label className="all-label" htmlFor="cargo_estagiario">
                    Cargo
                  </label>
                  <input
                    type="text"
                    name="cargo_estagiario"
                    className="nome-aprendiz"
                    id="cargo_estagiario"
                    disabled={isEmpresaDisabled}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.cargo_estagiario}
                  />
                </div>

                <div className="div-n-cpf-estagiario">
                  <label className="all-label" htmlFor="cpf_estagiario">
                    N° do CPF
                  </label>
                  <InputMask
                    mask="999.999.999-99"
                    maskChar=" "
                    type="text"
                    name="cpf_estagiario"
                    disabled={isEmpresaDisabled}
                    className="tce-two-row-cpf"
                    id="tce-two-row-cpf"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="tce-two-row">
                <div className="div-tce-formacao-escolar">
                  <label className="all-label" htmlFor="formacao_escolar">
                    Formação Escolar
                  </label>
                  <input
                    type="text"
                    name="formacao_escolar"
                    className="nome-aprendiz"
                    id="formacao_escolar"
                    onChange={handleChange}
                    value={formData.formacao_escolar}
                  />
                </div>

                <div className="div-tce-email">
                  <label className="all-label" htmlFor="email-supervisor">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email_supervisor"
                    className="tce-email-1"
                    maxLength={100}
                    id="email_supervisor"
                    onChange={handleChange}
                    value={formData.email_supervisor}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === Quem irá assinar o Termo de Compromisso? (separado do Supervisor) === */}
        <div className="tce-card">
          <div className="div-quem-assina-termo-title tce-card-header">
            <label htmlFor="quem-assina-termo-title">
              Quem irá assinar o Termo de Compromisso? (contrato do estagiário)
            </label>
          </div>

          <div className="tce-card-body">
            <div className="tce-two-row">
              <div className="div-nome-aprendiz">
                <label className="all-label" htmlFor="nome">
                  Nome
                </label>
                <input
                  type="text"
                  name="nome_termo_compromisso"
                  className="nome-aprendiz"
                  id="tce_nome_termo_compromisso"
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.nome_termo_compromisso}
                />
              </div>

              <div className="div-tce-email">
                <label className="all-label" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  className="tce-email-2"
                  id="email"
                  {...register("email")}
                  onChange={handleChange}
                  value={formData.email}
                />
              </div>
            </div>

            <div className="tce-two-row">
              <div className="div-nome-aprendiz">
                <label
                  className="all-label"
                  htmlFor="nome_telefone_estudante_aprovado"
                >
                  Nome e Telefone do Estudante aprovado:
                </label>
                <input
                  type="text"
                  name="nome_telefone_estudante_aprovado"
                  className="nome-aprendiz"
                  id="nome_telefone_estudante_aprovado"
                  onChange={handleChange}
                  value={formData.nome_telefone_estudante_aprovado}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div
            className="div-dados-estudante-estagiario-title tce-card-header"
            style={{ position: "relative" }}
          >
            <label htmlFor="dados-estudante-estagiario-title">
              Dados do(a) Estudante/Estagiário
            </label>
            {idadeCalculada !== null && (
              <span
                style={{
                  position: "absolute",
                  right: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "0.80rem",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  backgroundColor:
                    idadeCalculada >= 18 ? "#dcfce7" : "#fef08a",
                  color: idadeCalculada >= 18 ? "#166534" : "#854d0e",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                }}
              >
                {idadeCalculada >= 18 ? "Maior de Idade" : "Menor de Idade"}
              </span>
            )}
          </div>

          <div className="tce-card-body">
            <div className="tce-six">
              <div className="div-origem-candidato">
                <label className="all-label" htmlFor="origem_candidato">
                  Origem do Candidato *
                </label>
                <select
                  id="origem_candidato"
                  name="origem_candidato"
                  className="origem-candidato"
                  value={formData.origem_candidato || ""}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecione</option>
                  <option value="CIEEMG">Encaminhado pelo CIEEMG</option>
                  <option value="EMPRESA">Candidato Próprio da Empresa</option>
                </select>
              </div>

              <label className="tce-six-lbl">
                Caso não possua cadastro em nosso site, gentileza realizar em:{" "}
                <a href="https://www.cieemg.org.br/">www.cieemg.org.br</a>
              </label>

              {formData.origem_candidato === "CIEEMG" && (
                <div className="cenario-ci-eemg">
                  <div className="div-nome-estudante-estagiario">
                    <label
                      className="all-label"
                      htmlFor="nome_estagiario_cieemg"
                    >
                      Nome do Estudante/Estagiário *
                    </label>
                    <select
                      id="nome_estagiario_cieemg"
                      name="nome_estagiario_cieemg"
                      className="nome-estudante-estagiario"
                      onChange={handleChangeEstudanteCieemg}
                      disabled={loadingEstudantes || isEmpresaDisabled}
                      required
                      defaultValue=""
                    >
                      <option value="">Selecione um estudante</option>
                      {estudantesCieemg.map((est) => (
                        <option
                          key={est.id}
                          value={est.nome}
                          data-id={est.id}
                        >
                          {est.nome}
                        </option>
                      ))}
                    </select>
                    {loadingEstudantes && (
                      <small>Carregando lista de estudantes...</small>
                    )}
                  </div>

                  <div className="tce-six-1">
                    <div className="div-tce-cpf-estagiario">
                      <label className="all-label" htmlFor="cpf_estudante_estagiario_cieemg">
                        CPF
                      </label>
                      <InputMask
                        mask="999.999.999-99"
                        maskChar=" "
                        type="text"
                        name="cpf_estudante_estagiario"
                        className="tce-six-cpf"
                        id="cpf_estudante_estagiario_cieemg"
                        readOnly={isEmpresaDisabled}
                        onChange={handleChange}
                        value={formData.cpf_estudante_estagiario || ""}
                      />
                    </div>

                    <div className="div-tce-rg-estagiario">
                      <label className="all-label" htmlFor="rg">
                        RG
                      </label>
                      <input
                        type="text"
                        name="rg"
                        className="tce-six-rg"
                        id="rg"
                        readOnly={isEmpresaDisabled}
                        onChange={handleChange}
                        value={formData.rg || ""}
                      />
                    </div>

                    <div className="div-tce-telefone-estagiario">
                      <label
                        className="all-label"
                        htmlFor="telefone_estagiario"
                      >
                        Telefone
                      </label>
                      <InputMask
                        mask="(99)99999-9999"
                        maskChar=" "
                        type="text"
                        name="telefone_estagiario"
                        autoComplete="off"
                        className="tce-telefones"
                        id="telefone_estagiario"
                        readOnly={isEmpresaDisabled}
                        onChange={handleChange}
                        value={formData.telefone_estagiario || ""}
                      />
                    </div>

                    <div className="div-tce-email">
                      <label className="all-label" htmlFor="email_estagiario_cieemg">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email_estagiario"
                        className="tce-email-estagiario"
                        id="email_estagiario_cieemg"
                        maxLength={80}
                        readOnly={isEmpresaDisabled}
                        onChange={handleChange}
                        value={formData.email_estagiario || ""}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* === CENÁRIO EMPRESA: CPF + ciência quando não cadastrado === */}
              {formData.origem_candidato === "EMPRESA" && (
                <div className="cenario-empresa">
                  <div className="div-cpf-estudante">
                    <label
                      className="all-label"
                      htmlFor="cpf_estudante_estagiario_empresa"
                    >
                      CPF do Candidato *
                    </label>
                    <InputMask
                      mask="999.999.999-99"
                      maskChar=" "
                      type="text"
                      name="cpf_estudante_estagiario"
                      className="tce-six-cpf"
                      id="cpf_estudante_estagiario_empresa"
                      onChange={handleChangeCpfEmpresa}
                      value={formData.cpf_estudante_estagiario || ""}
                      disabled={loadingCpf || isEstudanteDisabled}
                      required
                    />
                    {loadingCpf && (
                      <small>Buscando candidato...</small>
                    )}
                  </div>

                  {estudanteEncontrado && (
                    <div className="alerta-ciente-sem-cadastro">
                      <strong>✓ Cadastro localizado no CIEEMG.</strong>{" "}
                      Os dados do estudante foram preenchidos
                      automaticamente.{" "}
                      {cienteSemCadastro && (
                        <em>
                          A Empresa confirma que prossegue mesmo sabendo
                          que o candidato possui cadastro.
                        </em>
                      )}
                    </div>
                  )}

                  {showConsentimento && !estudanteEncontrado && (
                    <div className="alerta-consentimento">
                      <label
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "0.5rem",
                          cursor:
                            isEstudanteDisabled
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={cienteSemCadastro}
                          onChange={(e) =>
                            setCienteSemCadastro(e.target.checked)
                          }
                          disabled={isEstudanteDisabled}
                          style={{ marginTop: "0.25rem" }}
                        />
                        <span>
                          Estou ciente de que este candidato{" "}
                          <strong>NÃO</strong> possui cadastro no CIEEMG
                          e prosseguirei com o preenchimento manual dos
                          dados do estudante/estagiário.
                        </span>
                      </label>
                    </div>
                  )}

                  {!estudanteEncontrado &&
                    cienteSemCadastro && (
                      <div className="dados-manuais-estagiario">
                        <div className="tce-two-row">
                          <div className="div-nome-estagiario">
                            <label
                              className="all-label"
                              htmlFor="nome_estagiario_empresa"
                            >
                              Nome do Estudante *
                            </label>
                            <input
                              type="text"
                              name="nome_estagiario"
                              className="nome-estudante-estagiario"
                              id="nome_estagiario_empresa"
                              maxLength={200}
                              readOnly={isEstudanteDisabled}
                              onChange={handleChange}
                              value={formData.nome_estagiario || ""}
                              required
                            />
                          </div>

                          <div className="div-tce-rg-estagiario">
                            <label
                              className="all-label"
                              htmlFor="rg_empresa"
                            >
                              RG
                            </label>
                            <input
                              type="text"
                              name="rg"
                              className="tce-six-rg"
                              id="rg_empresa"
                              readOnly={isEstudanteDisabled}
                              onChange={handleChange}
                              value={formData.rg || ""}
                            />
                          </div>
                        </div>

                        <div className="tce-two-row">
                          <div className="div-tce-telefone-estagiario">
                            <label
                              className="all-label"
                              htmlFor="telefone_estagiario_empresa"
                            >
                              Telefone
                            </label>
                            <InputMask
                              mask="(99)99999-9999"
                              maskChar=" "
                              type="text"
                              name="telefone_estagiario"
                              autoComplete="off"
                              className="tce-telefones"
                              id="telefone_estagiario_empresa"
                              readOnly={isEstudanteDisabled}
                              onChange={handleChange}
                              value={
                                formData.telefone_estagiario || ""
                              }
                            />
                          </div>

                          <div className="div-tce-email">
                            <label
                              className="all-label"
                              htmlFor="email_estagiario_empresa"
                            >
                              Email
                            </label>
                            <input
                              type="email"
                              name="email_estagiario"
                              className="tce-email-estagiario"
                              id="email_estagiario_empresa"
                              maxLength={80}
                              readOnly={isEstudanteDisabled}
                              onChange={handleChange}
                              value={formData.email_estagiario || ""}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* === Responsável Legal (condicional para menor de idade) === */}
              {idadeCalculada !== null && idadeCalculada < 18 && (
                <div
                  className="tce-card"
                  style={{
                    marginTop: "1rem",
                    marginBottom: "1rem",
                    border: "1px solid #fde68a",
                    backgroundColor: "#fffbeb",
                  }}
                >
                  <div className="tce-card-header">
                    <label>Responsável Legal do Estudante/Estagiário</label>
                    <small
                      style={{
                        marginLeft: "0.75rem",
                        fontWeight: "normal",
                        fontSize: "0.85rem",
                        opacity: 0.85,
                      }}
                    >
                      (Estudante menor de idade — {idadeCalculada} anos)
                    </small>
                  </div>
                  <div className="tce-card-body">
                    <div className="tce-two-row">
                      <div className="div-nome-aprendiz">
                        <label
                          className="all-label"
                          htmlFor="nome_menor_idade"
                        >
                          Nome:
                        </label>
                        <input
                          type="text"
                          name="nome_menor_idade"
                          className="nome-aprendiz"
                          id="nome_menor_idade"
                          maxLength={100}
                          onChange={handleChange}
                          value={formData.nome_menor_idade}
                        />
                      </div>

                      <div className="div-tce-email">
                        <label
                          className="all-label"
                          htmlFor="email_menor_idade"
                        >
                          Email:
                        </label>
                        <input
                          type="email"
                          name="email_menor_idade"
                          className="nome-aprendiz"
                          id="email_menor_idade"
                          maxLength={100}
                          onChange={handleChange}
                          value={formData.email_menor_idade}
                        />
                      </div>
                    </div>

                    <div className="tce-two-row">
                      <div className="div-tce-telefone-estagiario">
                        <label
                          className="all-label"
                          htmlFor="telefone_menor_idade"
                        >
                          Telefone:
                        </label>
                        <InputMask
                          mask="(99)99999-9999"
                          maskChar=" "
                          type="text"
                          name="telefone_menor_idade"
                          autoComplete="off"
                          className="tce-telefones"
                          id="telefone_menor_idade"
                          onChange={handleChange}
                          value={formData.telefone_menor_idade || ""}
                        />
                      </div>

                      <div className="div-tce-cpf-estagiario">
                        <label
                          className="all-label"
                          htmlFor="cpf_menor_idade"
                        >
                          CPF:
                        </label>
                        <InputMask
                          mask="999.999.999-99"
                          maskChar=" "
                          type="text"
                          name="cpf_menor_idade"
                          className="tce-six-cpf"
                          id="cpf_menor_idade"
                          onChange={handleChange}
                          value={formData.cpf_menor_idade || ""}
                        />
                      </div>
                    </div>

                    <div
                      className="alerta-responsavel-legal"
                      style={{ marginTop: "0.5rem" }}
                    >
                      ⚠️ O contrato do estagiário menor de idade deverá ser
                      assinado também pelo responsável legal.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-dados-estagiario-title tce-card-header">
            <label htmlFor="dados-estagiario-title">Dados do Estágio</label>
          </div>

          <div className="tce-card-body ">
            <div className="tce-four">
              <div className="tce-four-1">
                <div className="div-tce-four-1-inicio">
                  <label className="all-label" htmlFor="inicio_estagio">
                    Início
                  </label>
                  <input
                    type="date"
                    name="inicio_estagio"
                    className="inicio-estagio"
                    id="inicio-estagio"
                    disabled={isEmpresaDisabled}
                    maxLength={10}
                    onChange={handleChange}
                    value={formData.inicio_estagio}
                  />
                </div>

                <div className="div-tce-four-1-termino">
                  <label className="all-label" htmlFor="termino_estagio">
                    Término
                  </label>
                  <input
                    type="date"
                    name="termino_estagio"
                    className="termino-estagio"
                    id="termino-estagio"
                    disabled={isEmpresaDisabled}
                    maxLength={10}
                    min={formData.inicio_estagio || undefined}
                    max={calcularMaxTerminoEstagio()}
                    onChange={handleChange}
                    value={formData.termino_estagio}
                  />
                  {formData.inicio_estagio && (
                    <small
                      style={{
                        display: "block",
                        marginTop: "4px",
                        color: "#6c757d",
                      }}
                    >
                      Limite máximo (2 anos a partir do início):{" "}
                      <strong>
                        {formatarDataBR(calcularMaxTerminoEstagio())}
                      </strong>
                    </small>
                  )}
                </div>

                <div className="div-tce-four-1-valor-bolsa-auxilio">
                  <label className="all-label" htmlFor="valor_bolsa_auxilio">
                    Valor da Bolsa Auxílio
                  </label>
                  <NumericFormat
                    name="valor_bolsa_auxilio"
                    className="nome-aprendiz"
                    id="valor_bolsa_auxilio"
                    disabled={isEmpresaDisabled}
                    isAllowed={isPositiveNumber}
                    thousandSeparator={true}
                    value={formData.valor_bolsa_auxilio}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix="R$ "
                    onValueChange={(values) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        valor_bolsa_auxilio: values.formattedValue,
                      }));
                    }}
                  />
                </div>

                <div className="div-tce-four-1-valor-bolsa-transporte">
                  <label className="all-label" htmlFor="valor_bolsa_transporte">
                    Valor do Auxílio Transporte
                  </label>
                  <NumericFormat
                    name="valor_bolsa_transporte"
                    className="nome-aprendiz"
                    id="valor_bolsa_transporte"
                    disabled={isEmpresaDisabled}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix="R$"
                    value={formData.valor_bolsa_transporte}
                    onValueChange={(values) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        valor_bolsa_transporte: values.formattedValue,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="tce-four-2">
                <div className="div-tce-four-label">
                  <label className="tce-four-label" htmlFor="tce-four-label">
                    As datas de vigência poderão ser ajustadas pela equipe do
                    CIEEMG de acordo com as regras e exigências da instituição
                    de ensino.
                  </label>
                </div>

                <div className="dados-estagio-valor-bolsa-transporte">
                  <label className="all-label" htmlFor="local_estagio">
                    Local da realização do estágio
                  </label>
                  <input
                    type="text"
                    name="local_estagio"
                    className="nome-aprendiz"
                    id="local_estagio"
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.local_estagio}
                    readOnly={!permissao.podeEditarTudo}
                    title={
                      permissao.podeEditarTudo
                        ? "Campo preenchido automaticamente pela unidade selecionada. Edite manualmente se necessário."
                        : "Campo preenchido automaticamente conforme a unidade selecionada (Matriz/Filial)"
                    }
                  />
                </div>

                {permissao?.podeEditarEmpresa === true && (
                  <>
                    <div
                      className="div-local-realizacao"
                      id="tce-section-local"
                    >
                      <label className="all-label">Unidade</label>
                      <select
                        className="tca-form-control"
                        id="local_realizacao_atividades"
                        name="local_realizacao_atividades"
                        value={formData.local_realizacao_atividades}
                        onChange={(e) => {
                          const val = e.target.value;
                          console.log("[Filial] Unidade mudou para:", val);
                          handleChange(e);
                          // Ao trocar para FILIAL, auto-seleciona a primeira filial
                          // disponível para disparar o carregamento via GET /filiais/:id.
                          if (
                            val === "FILIAL" &&
                            (!localAtividade || localAtividade === "")
                          ) {
                            const primeiraFilial = filiais.find(
                              (f) =>
                                f.id !== 0 &&
                                f.id !== undefined &&
                                f.id !== null,
                            );
                            if (primeiraFilial) {
                              const id = String(primeiraFilial.id);
                              console.log(
                                "[Filial] auto-selecionando primeira filial:",
                                id,
                              );
                              setLocalAtividade(id);
                            }
                          }
                        }}
                        required
                      >
                        <option value="MATRIZ">Matriz</option>
                        {mostrarFiliais && (
                          <option value="FILIAL">Filial</option>
                        )}
                      </select>
                    </div>

                    {mostrarFiliais &&
                      formData.local_realizacao_atividades === "FILIAL" && (
                        <>
                          <div id="tce-section-filial" />
                          <hr className="tce-hr" />
                          <div
                            className="tca-col-md-12 tca-mb-3"
                            id="filial-info"
                          >
                            <label>
                              <h3>Informações da Filial</h3>
                            </label>
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>Nome da Filial</label>
                            <input
                              className="tca-form-control"
                              name="nome_filial"
                              value={formData.nome_filial}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>CPF/CNPJ</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="cpfCnpj"
                              value={formData.cpfCnpj}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>Bairro/Distrito</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="bairro"
                              value={formData.bairro}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>Municipio</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="municipio"
                              value={formData.municipio}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>UF</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="uf"
                              value={formData.uf}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>Telefone 1</label>
                            <InputMask
                              mask="(99)99999-9999"
                              maskChar=" "
                              className="tca-form-control"
                              name="telefones"
                              value={formData.telefones}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>Telefone 2</label>
                            <InputMask
                              mask="(99)99999-9999"
                              maskChar=" "
                              className="tca-form-control"
                              name="telefones2"
                              value={formData.telefones2}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-6 tca-mb-3">
                            <label>E-mail Contrato</label>
                            <input
                              type="email"
                              className="tca-form-control"
                              name="emailContratos"
                              value={formData.emailContratos}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>E-mail Financeiro</label>
                            <input
                              type="email"
                              className="tca-form-control"
                              name="emailFinanceiro"
                              value={formData.emailFinanceiro}
                              onChange={handleChange}
                            />
                          </div>

                          <div
                            className="tca-col-md-12 tca-mb-3"
                            id="filial-info"
                          >
                            <label>
                              <h3>Representante da Filial</h3>
                            </label>
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>Representante Por</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="representadaPor"
                              value={formData.representadaPor}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-2 tca-mb-3">
                            <label>Cargo</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="cargoRepresentante"
                              value={formData.cargoRepresentante}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>E-mail</label>
                            <input
                              type="email"
                              className="tca-form-control"
                              name="emailRepresentante"
                              value={formData.emailRepresentante}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>Responsvel/Supervisor do Estágio</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="responsavelSupervisor"
                              value={formData.responsavelSupervisor}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-2 tca-mb-3">
                            <label>Cargo</label>
                            <input
                              type="text"
                              className="tca-form-control"
                              name="cargoSupervisor"
                              value={formData.cargoSupervisor}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="tca-col-md-4 tca-mb-3">
                            <label>E-mail</label>
                            <input
                              type="email"
                              className="tca-form-control"
                              name="emailSupervisor"
                              value={formData.emailSupervisor}
                              onChange={handleChange}
                            />
                          </div>

                          {filialLoading && (
                            <div
                              className="tca-col-md-12 tca-mb-3"
                              style={{
                                color: "#475569",
                                fontStyle: "italic",
                                fontSize: "0.875rem",
                              }}
                            >
                              Carregando dados da filial…
                            </div>
                          )}
                        </>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-dias-horarios-title tce-card-header">
            <label htmlFor="dias-horarios-title">
              Dias e Horários da Realização do Estágio
            </label>
          </div>

          <div className="tce-card-body">
            <div className="tce-five">
              <div className="tce-five-1">
                <div className="div-tipo-contrato">
                  <label className="all-label" htmlFor="tipo_contrato">
                    Tipo de Contrato
                  </label>
                  <select
                    name="tipo_contrato"
                    className="tipo-contrato"
                    id="tipo-contrato"
                    onChange={handleChange}
                    required
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="PRESENCIAL">Estágio Presencial</option>
                    <option value="HIBRIDO">Estágio Híbrido</option>
                    <option value="HOMEOFFICE">Estágio Home Office</option>
                  </select>
                </div>

                <div className="div-horario-fixo-alternado">
                  <label
                    className="all-label"
                    htmlFor="horario_fixo_ou_alternado"
                  >
                    Horário fixo ou alternado
                  </label>
                  <select
                    name="horario_tipo"
                    className="tipo-contrato"
                    id="horario_tipo"
                    value={horario.tipo}
                    onChange={(e) =>
                      setHorario((prev) => ({ ...prev, tipo: e.target.value }))
                    }
                    required
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="fixo">Horário Fixo</option>
                    <option value="alternado">Horário Alternado</option>
                  </select>
                </div>
              </div>

              {horario.tipo === "fixo" && (
                <div className="horario-card tce-five-2">
                  <h4>
                    Horário Fixo{" "}
                    <small>(mesmo horário todos os dias selecionados)</small>
                  </h4>
                  <div className="horario-grid">
                    <div className="dias-semana">
                      <label>Dias da semana de atuação *</label>
                      {[
                        "Segunda",
                        "Terça",
                        "Quarta",
                        "Quinta",
                        "Sexta",
                        "Sábado",
                        "Domingo",
                      ].map((dia) => {
                        const key = dia
                          .toLowerCase()
                          .replace("ç", "c")
                          .replace("á", "a")
                          .replace("ã", "a");
                        return (
                          <label key={dia} className="checkbox-dia">
                            <input
                              type="checkbox"
                              checked={horario.diasSelecionados.includes(dia)}
                              onChange={(e) => {
                                const novos = e.target.checked
                                  ? [...horario.diasSelecionados, dia]
                                  : horario.diasSelecionados.filter(
                                      (d) => d !== dia,
                                    );
                                setHorario((prev) => ({
                                  ...prev,
                                  diasSelecionados: novos,
                                }));
                              }}
                            />
                            {dia}
                          </label>
                        );
                      })}
                    </div>

                    <div className="horarios">
                      <div>
                        <label>Horário de entrada *</label>
                        <input
                          type="time"
                          value={horario.fixo.entrada}
                          onChange={(e) =>
                            setHorario((prev) => ({
                              ...prev,
                              fixo: { ...prev.fixo, entrada: e.target.value },
                            }))
                          }
                          required
                        />
                      </div>
                      <div>
                        <label>Horário de saída *</label>
                        <input
                          type="time"
                          value={horario.fixo.saida}
                          onChange={(e) =>
                            setHorario((prev) => ({
                              ...prev,
                              fixo: { ...prev.fixo, saida: e.target.value },
                            }))
                          }
                          required
                        />
                      </div>
                      <div>
                        <label>Intervalo (opcional até 6h)</label>
                        <div className="intervalo-inputs">
                          <input
                            type="time"
                            value={horario.fixo.intervaloInicio}
                            onChange={(e) =>
                              setHorario((prev) => ({
                                ...prev,
                                fixo: {
                                  ...prev.fixo,
                                  intervaloInicio: e.target.value,
                                },
                              }))
                            }
                          />
                          <span> às </span>
                          <input
                            type="time"
                            value={horario.fixo.intervaloFim}
                            onChange={(e) =>
                              setHorario((prev) => ({
                                ...prev,
                                fixo: {
                                  ...prev.fixo,
                                  intervaloFim: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {horario.tipo === "alternado" && (
                <div className="horario-card tce-five-2">
                  <h4>
                    Horário Alternado{" "}
                    <small>(horários diferentes por dia)</small>
                  </h4>
                  <table className="horario-grade">
                    <thead>
                      <tr>
                        <th>Dia da semana</th>
                        <th>Horário de entrada</th>
                        <th>Horário de saída</th>
                        <th>Intervalo (opcional até 6h)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: "segunda", label: "Segunda" },
                        { key: "terca", label: "Terça" },
                        { key: "quarta", label: "Quarta" },
                        { key: "quinta", label: "Quinta" },
                        { key: "sexta", label: "Sexta" },
                        { key: "sabado", label: "Sábado" },
                        { key: "domingo", label: "Domingo" },
                      ].map((dia) => (
                        <tr key={dia.key}>
                          <td>{dia.label}</td>
                          <td>
                            <input
                              type="time"
                              value={horario.alternado[dia.key].entrada}
                              onChange={(e) =>
                                setHorario((prev) => ({
                                  ...prev,
                                  alternado: {
                                    ...prev.alternado,
                                    [dia.key]: {
                                      ...prev.alternado[dia.key],
                                      entrada: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={horario.alternado[dia.key].saida}
                              onChange={(e) =>
                                setHorario((prev) => ({
                                  ...prev,
                                  alternado: {
                                    ...prev.alternado,
                                    [dia.key]: {
                                      ...prev.alternado[dia.key],
                                      saida: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <div className="intervalo-inputs">
                              <input
                                type="time"
                                value={
                                  horario.alternado[dia.key].intervaloInicio
                                }
                                onChange={(e) =>
                                  setHorario((prev) => ({
                                    ...prev,
                                    alternado: {
                                      ...prev.alternado,
                                      [dia.key]: {
                                        ...prev.alternado[dia.key],
                                        intervaloInicio: e.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                              <span> às </span>
                              <input
                                type="time"
                                value={horario.alternado[dia.key].intervaloFim}
                                onChange={(e) =>
                                  setHorario((prev) => ({
                                    ...prev,
                                    alternado: {
                                      ...prev.alternado,
                                      [dia.key]: {
                                        ...prev.alternado[dia.key],
                                        intervaloFim: e.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {horario.tipo && (
                <div className="carga-horaria-info">
                  {(() => {
                    const { errors, totalSemanal } = validarHorarios(horario);
                    return (
                      <>
                        {totalSemanal <= 30 ? (
                          <p className="carga-ok">
                            Carga horária semanal: {totalSemanal.toFixed(1)}
                            h/semana
                          </p>
                        ) : (
                          <p className="erro-carga">
                            Carga horária semanal: {totalSemanal.toFixed(1)}
                            h/semana ⚠️ Limite máximo permitido: 30h
                          </p>
                        )}
                        {errors.length > 0 && (
                          <ul className="horario-errors">
                            {errors.map((err, i) => (
                              <li key={i}>⚠️ {err}</li>
                            ))}
                          </ul>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="div-fundo-txa">
                <div className="div-txa-obs-ajuste">
                  <label
                    className="textarea-observacoes-lbl"
                    htmlFor="atividades_desenvolvidas"
                  >
                    Atividades a serem desenvolvidas
                  </label>
                  <textarea
                    style={{ resize: "none" }}
                    className="textarea-observacoes"
                    htmlFor="atividades_desenvolvidas"
                    name="atividades_desenvolvidas"
                    id="textarea-observacoes"
                    placeholder="Digite aqui."
                    onChange={(e) => {
                      const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
                      e.target.value = e.target.value.replace(regex, "");
                      handleChange(e);
                    }}
                    value={formData.atividades_desenvolvidas}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="tce-card">
            <div className="div-dias-horarios-title tce-card-header">
              <label htmlFor="dias-horarios-title">
                Comprovante de Experiência do Supervisor de Estágio
              </label>
            </div>

            <div className="tce-card-body">
              <span id="cpv-id">
                Caso o supervisor não possua formação compatível com a área do
                curso do estudante, mas possua experiência comprovada na área de
                atuação, anexe abaixo o documento comprobatório.
              </span>
              <div className="div-incompatibilidade">
                <label className="checkbox-incompatibilidade">
                  <input
                    type="checkbox"
                    id="possui-incompatibilidade"
                    checked={possuiIncompatibilidade}
                    onChange={(e) =>
                      setPossuiIncompatibilidade(e.target.checked)
                    }
                  />
                  Supervisor possui formação <strong>incompatível</strong> com a
                  área do curso do estudante
                </label>
              </div>

              <div className="upload-supervisor">
                <label>
                  Documento comprobatório de experiência do supervisor
                  {possuiIncompatibilidade && (
                    <span style={{ color: "red" }}> *</span>
                  )}
                </label>
                <small>
                  {possuiIncompatibilidade
                    ? "Obrigatório: a formação do supervisor não é compatível com a área do curso."
                    : "Opcional: anexe apenas se necessário."}
                </small>

                <div className="upload-box">
                  <div className="upload-info">
                    <strong>Selecione um arquivo</strong>
                    <span> ou arraste e solte aqui</span>
                    <small>
                      Formatos permitidos: PDF, JPG, JPEG, PNG
                      <br />
                      Tamanho máximo: 10MB
                    </small>
                    <input
                      id="arquivo-supervisor"
                      ref={inputSupervisorRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleArquivoSupervisor}
                      hidden
                    />
                    <label htmlFor="arquivo-supervisor" className="btn-upload">
                      Selecionar arquivo
                    </label>
                  </div>
                </div>

                {arquivoSupervisor && (
                  <>
                    <h4 style={{ marginTop: "25px" }}>Arquivo anexado</h4>
                    <div className="arquivo-enviado">
                      <div className="arquivo-info">
                        <strong>{arquivoSupervisor.name}</strong>
                        <span>
                          {(arquivoSupervisor.size / 1024).toFixed(0)} KB
                        </span>
                      </div>

                      <div className="arquivo-acoes">
                        <button
                          type="button"
                          onClick={visualizarArquivoSupervisor}
                        >
                          Visualizar
                        </button>
                        <button
                          type="button"
                          onClick={removerArquivoSupervisor}
                        >
                          Remover
                        </button>
                      </div>
                    </div>

                    <div className="info-final">
                      <strong>Importante:</strong> O documento anexado será
                      encaminhado para análise da equipe do CIEEMG.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-dados-escolares-title tce-card-header">
            <label htmlFor="dados-escolares-title">Dados Escolares</label>
          </div>

          <div className="tce-card-body">
            <div className="tce-eight">
              <div className="tce-eight-1">
                <div className="div-escola-faculdade">
                  <label className="all-label" htmlFor="escola">
                    Escola/Faculdade
                  </label>
                  <input
                    type="text"
                    name="escola"
                    className="escola-faculdade"
                    id="escola-faculdade"
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.escola}
                  />
                </div>

                <div className="div-unidade">
                  <label className="all-label" htmlFor="unidade">
                    Unidade
                  </label>
                  <input
                    type="text"
                    name="unidade"
                    className="tce-eight-unidade"
                    id="tce-eight-unidade"
                    maxLength={100}
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.unidade}
                  />
                </div>

                <div className="div-curso">
                  <label className="all-label" htmlFor="curso">
                    Curso
                  </label>
                  <input
                    type="text"
                    name="curso"
                    className="tce-eight-curso"
                    id="tce-eight-curso"
                    maxLength={100}
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.curso}
                  />
                </div>
              </div>

              <div className="tce-eight-2">
                <div className="div-ano-periodo-serie">
                  <label className="all-label" htmlFor="ano_periodo_serie">
                    Ano, período ou série
                  </label>
                  <input
                    type="text"
                    name="ano_periodo_serie"
                    className="ano-periodo-serie"
                    id="ano_periodo_serie"
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.ano_periodo_serie}
                  />
                </div>

                <div className="div-previsao-formatura">
                  <label className="all-label" htmlFor="previsao_formatura">
                    Previsão de Formatura
                  </label>
                  <input
                    type="text"
                    name="previsao_formatura"
                    className="previsao-formatura"
                    id="tce_previsao_formatura"
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.previsao_formatura}
                  />
                </div>

                <div className="div-tipo-estagio">
                  <label className="all-label" htmlFor="tce-eight-tipo-estagio">
                    Tipo de estágio é obrigatório?
                  </label>
                  <select
                    name="tipo_estagio"
                    id="tipo_estagio"
                    onChange={handleChange}
                    disabled={isEstudanteDisabled}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </div>
              </div>

              <div className="tce-eight-3">
                <div className="div-orientador-estagio" id="orientador-estagio">
                  <label className="all-label" htmlFor="orientador_estagio">
                    Orientador de Estágio / Responsável pelo Estágio /
                    Coordenador de Estágio ou de Curso (Escola/Faculdade)
                  </label>
                  <input
                    type="text"
                    name="orientador_estagio"
                    className="orientador-estagio"
                    id="orientador-estagio"
                    onChange={handleChange}
                    value={formData.orientador_estagio}
                    disabled={isEstudanteDisabled}
                  />
                </div>
              </div>

              <div className="tce-eight-4">
                <div className="div-cargo">
                  <label className="all-label" htmlFor="cargo">
                    Cargo
                  </label>
                  <input
                    type="text"
                    name="cargo"
                    className="ano-periodo-serie"
                    id="cargo"
                    onChange={handleChange}
                    value={formData.cargo}
                    disabled={isEstudanteDisabled}
                  />
                </div>

                <div className="div-tce-eight-email">
                  <label className="all-label" htmlFor="email_escola">
                    E-mail
                  </label>
                  <input
                    type="email"
                    name="email_escola"
                    className="tce-eight-email"
                    id="email_escola"
                    onChange={handleChange}
                    value={formData.email_escola}
                    disabled={isEstudanteDisabled}
                  />
                </div>

                <div className="div-tce-eight-cnpj" id="tce-eight-cnpj">
                  <label className="all-label" htmlFor="cnpj_escola">
                    CNPJ
                  </label>
                  <InputMask
                    mask="999.999/9999-999"
                    maskChar=" "
                    type="text"
                    name="cnpj_escola"
                    className="tce-eight-cnpj"
                    id="tce-eight-cnpj"
                    onChange={handleChange}
                    value={formData.cnpj_escola}
                    disabled={isEstudanteDisabled}
                    required
                  />
                </div>
              </div>

              <label className="lbl-tce-eight-observacoes">
                Observações: <br />
                1) O estudante não deve iniciar o estágio antes da assinatura do
                TCE; <br />
                2) Solicitar ao estudante uma declaração de matrícula e
                frequência emitida pela escola; <br />
                3) Em casos de alteração de endereço da empresa, favor informar.
              </label>
            </div>
          </div>
        </div>

        <div style={{ padding: "0 1rem 1rem 1rem", boxSizing: "border-box" }}>
          <div className="tce-nine-1" style={{ marginTop: "0.25rem", textAlign: "center" }}>
            <label htmlFor="" style={{ color: "#fff" }}>
              Em caso de dúvidas ou necessidade de mais orientações ligue
              para o telefone/Whatsapp: (31)3429-8100 ou envie um e-mail
              para atendimento@cieemg.org.br
            </label>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-orientacao-imposto-title tce-card-header">
            <label htmlFor="ajuste-emissao-contrato-title">
              Atenção - Orientações sobre o imposto de renda para bolsas
              liberadas via CIEE/MG
            </label>
          </div>

          <div className="tce-card-body">
            <div className="div-tce-ten">
              <div className="div-tce-ten-1">
                <img src="./imagem-incidencia-mensal.png" alt="" />
                <div className="text-container">
                  <span className="span-1">
                    Conforme as regras de retenção de Imposto de Renda vigentes,
                    empresas em que o CIEE/MG realiza o repasse dos valores de
                    Bolsa - Auxílio, o teto máximo que o CIEE/MG pode liberar
                    por estagiário não pode ultrapassar o valor total de
                    R$2.824,00
                  </span>
                  <span className="span-2">
                    Caso o valor total de todos os valores liberados para o
                    estagiário ultrapasse esse teto, a retenção de Imposto de
                    Renda deve ser realizado pela liberação do recurso para
                    pagamento. *Manual eSocial *Medida Provisória nº 1206, de 06
                    de fevereiro de 2024 Lei nº 14.663, de 28 de Agosto de 2023
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === Anexos (DocumentUploadForm) - posicionado no final do formulário === */}
        <DocumentUploadForm
          schema={[
            {
              id: "declaracao_escolar",
              label: "Declaração escolar e/ou histórico de conclusão do Ensino Médio",
              hint: "Até 30 dias de emissão",
              icon: "GraduationCap",
              required: true,
            },
            {
              id: "carga_horaria",
              label: "Declaração constando carga horária obrigatória",
              icon: "FileText",
              required: true,
            },
            {
              id: "documento_identificacao",
              label: "Documento de Identificação (RG ou CNH)",
              icon: "IdCard",
              required: true,
            }
          ]}
          filesById={anexosFiles}
          statusById={anexosStatus}
          onAnexar={(id, file) =>
            handleAnexoChange(id)(file)
          }
          topNote="Gentileza anexar sua declaração escolar atualizada ou com no máximo 30 dias de emissão. Sem a declaração escolar sua contratação não será efetivada."
          missingMessage="Anexe a declaração escolar atualizada, com no máximo 30 dias de emissão. Sem ela, sua contratação não será efetivada."
          observations={formData.observacoes2}
          insertObservations={showObs2}
          onInsertObservationsChange={(v) => {
            setShowObs2(v);
            setFormData((p) => ({
              ...p,
              observacoes2: v ? p.observacoes2 : "",
            }));
          }}
          onObservationsChange={(v) => {
            const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
            const filtered = String(v).replace(regex, "");
            setFormData((p) => ({ ...p, observacoes2: filtered }));
          }}
        />

        {/* === CIEE/MG - Particularidades para emissão do contrato === */}
        {/* Movido para o final do formulário e edição restrita ao perfil SEOPE. */}
        <div className="tce-card">
          <div className="tce-card-body">
            <div
              className="div-observacoes"
              style={{
                background: "rgba(30, 41, 59, 0.55)",
                borderRadius: 10,
                padding: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                border: "1px solid rgba(255, 255, 255, 0.06)",
                boxSizing: "border-box",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 4,
                      height: 16,
                      borderRadius: 2,
                      background: "#185fa5",
                    }}
                  />
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      margin: 0,
                      color: "#fff",
                      letterSpacing: 0.2,
                    }}
                  >
                    CIEE/MG - Particularidades para emissão do contrato
                  </p>
                </div>
                {nivel !== 2 && (
                  <span
                    title="Somente o perfil SEOPE pode editar este bloco"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "4px 10px",
                      borderRadius: 12,
                      background: "rgba(148, 163, 184, 0.18)",
                      color: "#cbd5e1",
                      border: "1px solid rgba(148, 163, 184, 0.35)",
                      letterSpacing: 0.4,
                    }}
                  >
                    SOMENTE SEOPE
                  </span>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  disabled={nivel !== 2}
                  onClick={() => {
                    handleChange({ target: { name: "observacoes1", value: "inserir" } });
                    setShowObs1(true);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "0.65rem 1rem",
                    borderRadius: 8,
                    border: showObs1
                      ? "1px solid #01336d"
                      : "1px solid rgba(255, 255, 255, 0.18)",
                    background: showObs1 ? "#01336d" : "rgba(255, 255, 255, 0.04)",
                    color: showObs1 ? "#ffffff" : "#e7e5e4",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    cursor: nivel === 2 ? "pointer" : "not-allowed",
                    opacity: nivel === 2 ? 1 : 0.5,
                    transition:
                      "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease",
                  }}
                >
                  {showObs1 && <Check size={13} />}
                  Inserir Particularidades para emissão do contrato
                </button>
                <button
                  type="button"
                  disabled={nivel !== 2}
                  onClick={() => {
                    handleChange({ target: { name: "observacoes1", value: "nao-inserir" } });
                    setShowObs1(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "0.65rem 1rem",
                    borderRadius: 8,
                    border: !showObs1
                      ? "1px solid #01336d"
                      : "1px solid rgba(255, 255, 255, 0.18)",
                    background: !showObs1 ? "#01336d" : "rgba(255, 255, 255, 0.04)",
                    color: !showObs1 ? "#ffffff" : "#e7e5e4",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    cursor: nivel === 2 ? "pointer" : "not-allowed",
                    opacity: nivel === 2 ? 1 : 0.5,
                    transition:
                      "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease",
                  }}
                >
                  {!showObs1 && <Check size={13} />}
                  Não Inserir particularidades para emissão do contrato
                </button>
              </div>

              <div className="div-txa-obs-ajuste-observacoes1">
                {showObs1 && (
                  <textarea
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      padding: 10,
                      fontSize: 13,
                      fontFamily: "inherit",
                      resize: "none",
                      background: "#ffffff",
                      color: "#0f172a",
                      outline: "none",
                      transition: "border-color 0.18s ease",
                    }}
                    className="textarea-observacoes"
                    name="observacoes1"
                    placeholder="Digite aqui."
                    disabled={nivel !== 2}
                    onChange={(e) => {
                      if (nivel !== 2) return;
                      const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
                      e.target.value = e.target.value.replace(regex, "");
                      handleChange(e);
                    }}
                    value={formData.observacoes1 === "inserir" ? "" : formData.observacoes1}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {modoEdicao && tceHistorico.length > 0 && (
          <div className="tce-historico">
            <h3>Histórico de Transições</h3>
            <ul>
              {tceHistorico.map((h, i) => (
                <li key={i}>
                  <strong>{h.status_anterior || "início"}</strong> →{" "}
                  <strong>{h.status_novo}</strong>
                  <br />
                  <small>
                    Por: {h.usuario_nome || h.usuario_id} ({h.usuario_tipo})
                  </small>
                  <br />
                  <small>
                    {new Date(h.created_at).toLocaleString("pt-BR")}
                  </small>
                  {h.observacao && <div>📝 {h.observacao}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="btn-wrapper">
          {!modoEdicao && (
            <button
              type="submit"
              className="btn-cadastrar-tce"
              disabled={
                !permissao.podeEditarEmpresa && !permissao.podeEditarEstudante
              }
            >
              Cadastrar
            </button>
          )}

          {modoEdicao && (
            <>
              {tceStatus === "AGUARDANDO_ESTUDANTE" &&
                permissao.podeSubmeter && (
                  <button onClick={submeterValidacaoEstudante}>
                    Submeter para CIEEMG
                  </button>
                )}

              {tceStatus === "EM_VALIDACAO" && permissao.podeAprovar && (
                <>
                  <button onClick={aprovar} className="btn-aprovar">
                    Aprovar
                  </button>
                  {permissao.podeReprovar && (
                    <button onClick={recusar} className="btn-recusar">
                      Reprovar
                    </button>
                  )}
                </>
              )}

              {tceStatus === "AGUARDANDO_CONFECCAO" &&
                permissao.podeAprovar && (
                  <button
                    type="button"
                    onClick={marcarConfeccionado}
                    className="btn-cadastrar-tce"
                  >
                    Marcar Contrato como Confeccionado
                  </button>
                )}

              {tceStatus === "CONTRATO_CONFECCIONADO" &&
                permissao.podeAprovar && (
                  <button
                    type="button"
                    onClick={marcarDisponibilizado}
                    className="btn-cadastrar-tce"
                  >
                    Marcar como Disponibilizado para Assinatura
                  </button>
                )}

              {tceStatus === "AGUARDANDO_ASSINATURAS" &&
                permissao.podeAprovar && (
                  <button
                    type="button"
                    onClick={finalizar}
                    className="btn-aprovar"
                  >
                    Finalizar Processo
                  </button>
                )}
            </>
          )}
        </div>
      </form>

      {/* Container de Toasts (substitui alert() por notificações elegantes) */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {/* Diálogo de confirmação elegante (substitui window.confirm/confirm) */}
      {confirmDialog}
    </>
  );
};
const TCEWrapper = () => {
  const currentUser = JSON.parse(localStorage.getItem("user"));

  if (Number(currentUser?.acesso_autorizacao ?? 0) === 1) {
    return <AguardandoEmpresa />;
  }
  return <TCE />;
};

export default TCEWrapper;
